<template>
  <div v-if="isVisible" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
    <div class="relative bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-1/3 max-h-[80vh] overflow-y-auto">

      <i
        @click="closeModal"
        class="fas fa-times absolute top-4 right-4 text-gray-500 dark:text-gray-300 cursor-pointer hover:text-gray-700 dark:hover:text-gray-100 transition duration-150 text-2xl"
        title="Close"
      ></i>

      <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">Rearrange Speakers</h2>
      <h6 class="text-sm mb-3 text-gray-700 dark:text-gray-300">
        Drag and drop speakers below to reorder them, or move them between categories
      </h6>

      <hr class="border-gray-300 dark:border-gray-600">

      <div class="dragula-container">
        <div
          v-for="(speakers, category) in localSpeakersGroupedByCategory"
          :key="category"
          class="category-group"
        >
        <div
          class="text-left category-header px-4 py-2 font-semibold text-gray-600 dark:text-gray-300"
          :class="{ 'cursor-grab': category !== 'No Category' }"
        >
          {{ category }}
          <i 
            class="text-black dark:text-gray-300 fill-current fas fa-caret-up text-2xl" style="float: right;" @click="moveCategoryUp(category)">
          </i>
          <i 
            class="text-black dark:text-gray-300 fill-current fas fa-caret-down text-2xl" style="float: right;" @click="moveCategoryDown(category)">
          </i>
        </div>

          <div class="speakers-list" :data-category="category">
            <div
              v-for="speaker in speakers"
              :key="speaker.fees.id"
              :data-id="speaker.fees.id"
              class="text-left speaker-item mx-4 pb-2 rounded cursor-grab dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <i 
                class="fill-current fas fa-grip-lines mr-2 text-gray-500 dark:text-gray-400">
              </i>
              {{ speaker.speaker.speaker_name }}
            </div>
          </div>

          <hr class="border-gray-300 dark:border-gray-600">
        </div>
      </div>

      <div class="flex justify-end gap-4 mt-6">
        <button
          @click="closeModal"
          class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md dark:bg-gray-700 dark:text-gray-300"
        >
          Cancel
        </button>
        <button
          @click="saveRearrangedSpeakers"
          class="px-4 py-2 bg-blue-500 text-white rounded-md dark:bg-blue-600 dark:text-gray-100"
        >
          Save
        </button>
      </div>
    </div>
  </div>
</template>

<script>

import { ElMessage } from 'element-plus';

export default {
  name: 'RearrangeSpeakersModal',
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    // Store the original speakers data passed from the parent component
    speakersGroupedByCategory: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      // Local copy of the speaker data. Lets us rearrange speakers without mutating the original data
      // until the user chooses to save.
      localSpeakersGroupedByCategory: []
    }
  },
  watch: {
    // When the modal becomes visible, copy the original data to the local data.
    isVisible(newVal) {
      if (newVal) {
        this.localSpeakersGroupedByCategory = JSON.parse(
          JSON.stringify(this.speakersGroupedByCategory)
        );

        this.$nextTick(() => {
          this.setupDragula();
        });
      }
    },
  },
  methods: {
    closeModal() {
      this.$emit("update:isVisible", false);
    },
    setupDragula()
    {
      // Define the areas the user can drag and drop speakers into.
      const containers = Array.from(
        document.querySelectorAll('.dragula-container, .speakers-list')
      );

      const drake = dragula(containers, {
        moves: (el, source, handle) => {

          // Prevent dragging "No Category"
          if (el.classList.contains('category-group')) {
            const categoryHeader = el.querySelector('.category-header').textContent.trim();
            if (categoryHeader === 'No Category') {
              return false;
            }
          }

          // Allow dragging for category headers and speaker items.
          return (
            handle.classList.contains('category-header') ||
            el.classList.contains('speaker-item')
          );
        },
        accepts: (el, target) => {
          // Allow category-group to be dropped only in the dragula-container.
          if (el.classList.contains('category-group')) {
            return target.classList.contains('dragula-container');
          }

          // Allow speaker-item to be dropped only in speakers-list.
          if (el.classList.contains('speaker-item')) {
            return target.classList.contains('speakers-list');
          }

          return false;
        },
        direction: 'vertical',
      });
      
      // Update the local list when a speaker is dropped.
      drake.on('drop', (el, target, source, sibling) => {
        if (el.classList.contains('category-group')) {

          if (sibling == null) {

            // Someone has tried to drop a category below the 'No Category' which we want to
            // force to always be last.
            drake.cancel(true);

            ElMessage({
              type: 'warning',
              message: "'No Category' always appears last and cannot be moved.",
              duration: 3000,
            });

            return;
          }

          // Handle category reordering.
          this.handleCategoryDrop(el, sibling);
        } else if (el.classList.contains('speaker-item')) {
          // Handle speaker reordering.
          this.handleDrop(el, target, source, sibling);
        }
      });
    },
    handleDrop(el, target, source, sibling) {
      const originalCategory = source.getAttribute('data-category');
      const newCategory = target.getAttribute('data-category');
      const speakerFeeId = el.getAttribute('data-id');

      // Find the speaker in the source category.
      const sourceSpeakers = this.localSpeakersGroupedByCategory[originalCategory];
      const movedSpeaker = sourceSpeakers.find(
        (speaker) => speaker.fees.id === speakerFeeId
      );

      // Remove the speaker from the source category.
      this.localSpeakersGroupedByCategory[originalCategory] = sourceSpeakers.filter(
        (speaker) => speaker.fees.id !== speakerFeeId
      );

      // Add the speaker to the target category.
      const targetSpeakers = this.localSpeakersGroupedByCategory[newCategory];
      if (movedSpeaker) {
        // If there's a sibling, insert the speaker before it.
        if (sibling) {
          const siblingId = sibling.getAttribute('data-id');
          const siblingIndex = targetSpeakers.findIndex(
            (speaker) => speaker.fees.id === siblingId
          );
          targetSpeakers.splice(siblingIndex, 0, movedSpeaker);
        } else {
          // Otherwise, add the speaker to the end of the list.
          targetSpeakers.push(movedSpeaker);
        }

        // Also update the speaker's category in the local data.
        movedSpeaker.fees.Category = newCategory;
      }

      // Update local data to reflect the changes.
      this.localSpeakersGroupedByCategory[originalCategory] = this.localSpeakersGroupedByCategory[originalCategory];
      this.localSpeakersGroupedByCategory[newCategory] = targetSpeakers;      
    },
    handleCategoryDrop(el, sibling) {
      const draggedCategory = el.querySelector('.category-header').textContent.trim();

      // Find the index of the dragged category.
      const categories = Object.keys(this.localSpeakersGroupedByCategory);
      const draggedIndex = categories.indexOf(draggedCategory);
      const noCategoryIndex = categories.indexOf('No Category');

      // Remove the dragged category from its original position.
      const [removedCategory] = categories.splice(draggedIndex, 1);

      // Find the index of the sibling category (if any).
      if (sibling) {
        const siblingCategory = sibling.querySelector('.category-header').textContent.trim();
        const siblingIndex = categories.indexOf(siblingCategory);

        // Insert the dragged category before the sibling.
        categories.splice(siblingIndex, 0, removedCategory);
      } else {
        // If no sibling, append the dragged category to the end.
        categories.push(removedCategory);
      }

      // Rebuild the localSpeakersGroupedByCategory object with the new order.
      const reorderedCategories = {};
      categories.forEach((category) => {
        reorderedCategories[category] = this.localSpeakersGroupedByCategory[category];
      });

      this.localSpeakersGroupedByCategory = reorderedCategories;
    },
    moveCategoryUp(category) {
      // Find the index of the dragged category.
      const categories = Object.keys(this.localSpeakersGroupedByCategory);
      const currentIndex = categories.indexOf(category);

      // Remove the moved category from its original position.
      const [removedCategory] = categories.splice(currentIndex, 1);

      // Set the new position to 1 higher (1 higher in the UI which is -1 index). If its already at 0 it will stay at 0
      let newIndex = currentIndex - 1;
      if(newIndex < 0){
        newIndex = 0;
      }

      // Insert the moved category 1 to the new index.
      categories.splice(newIndex, 0, removedCategory);

      // Rebuild the localSpeakersGroupedByCategory object with the new order.
      const reorderedCategories = {};
      categories.forEach((category) => {
        reorderedCategories[category] = this.localSpeakersGroupedByCategory[category];
      });

      this.localSpeakersGroupedByCategory = reorderedCategories;
    },
    moveCategoryDown(category) {
      // Find the index of the category to move down.
      const categories = Object.keys(this.localSpeakersGroupedByCategory);
      const currentIndex = categories.indexOf(category);

      // Remove the moved category from its original position.
      const [removedCategory] = categories.splice(currentIndex, 1);

      // Find the index of the new position if its not at the end, otherwise just put it at the end.
      if (currentIndex <= categories.length) {
        const newIndex = currentIndex + 1;

        // Insert the moved category after its current possition.
        categories.splice(newIndex, 0, removedCategory);
      } else {
        categories.push(removedCategory);
      }

      // Rebuild the localSpeakersGroupedByCategory object with the new order.
      const reorderedCategories = {};
      categories.forEach((category) => {
        reorderedCategories[category] = this.localSpeakersGroupedByCategory[category];
      });

      this.localSpeakersGroupedByCategory = reorderedCategories;
    },
    saveRearrangedSpeakers() {
      // Emit the rearranged speakers to the parent component
      this.$emit('save', this.localSpeakersGroupedByCategory);
    },
  },
};
</script>

<style scoped>
  /* Ensure there is always at least some space to drop a speaker into */
  .speakers-list {
    min-height: 32px;
  }
</style>